'use client';

import { useEffect, useRef } from 'react';
import Script from 'next/script';
import { useLogger, Logger } from '@/hooks/useLogger';
import { useSearchParams } from 'next/navigation';
import { useLanguageStore } from '@/store/languageStore';
import { LOCAL_STORAGE_KEYS, localStorage as storage } from '@/constants/localStorage';

// Helper function to extract userId from different LIFF methods
const extractUserIdFromLiff = (): string | null => {
  try {
    // Check if window.liff exists and is initialized
    if (!window?.liff) {
      console.warn("LIFF SDK not available on window object");
      return null;
    }

    // Method 1: From context
    try {
      if (typeof window.liff.getContext === 'function') {
        const context = window.liff.getContext() as { userId?: string };
        if (context && context.userId) {
          console.log("UserId found from getContext():", context.userId);
          return context.userId;
        }
      } else {
        console.warn("getContext() method not available in this LIFF version");
      }
    } catch (contextError) {
      console.warn("getContext() method failed:", contextError);
    }

    // Method 2: From decoded ID token
    try {
      if (typeof window.liff.getDecodedIDToken === 'function') {
        const decodedToken = window.liff.getDecodedIDToken() as { sub?: string } | null;
        if (decodedToken && decodedToken.sub) {
          console.log("UserId found from getDecodedIDToken():", decodedToken.sub);
          return decodedToken.sub;
        }
      } else {
        console.warn("getDecodedIDToken() method not available in this LIFF version");
      }
    } catch (tokenError) {
      console.warn("getDecodedIDToken() method failed:", tokenError);
    }

    // Method 3: From access token (JWT decode)
    try {
      if (typeof window.liff.getAccessToken === 'function') {
        const accessToken = window.liff.getAccessToken();
        if (accessToken && typeof accessToken === 'string') {
          try {
            const tokenParts = accessToken.split('.');
            if (tokenParts.length === 3) {
              const payload = JSON.parse(atob(tokenParts[1]));
              if (payload.sub) {
                console.log("UserId found from getAccessToken():", payload.sub);
                return payload.sub;
              }
            }
          } catch (decodeError) {
            console.warn("Failed to decode JWT access token:", decodeError);
          }
        }
      } else {
        console.warn("getAccessToken() method not available in this LIFF version");
      }
    } catch (accessTokenError) {
      console.warn("getAccessToken() method failed:", accessTokenError);
    }

    console.warn("No userId found from any LIFF method");
    return null;
  } catch (error) {
    console.error("Critical error in extractUserIdFromLiff:", error);
    return null;
  }
};

// Helper function to validate and store profile data
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const storeProfileData = (profile: any, userId: string, logger: Logger, liffId: string): boolean => {
  try {
    // Store userId (always required)
    storage.setItem(LOCAL_STORAGE_KEYS.LINE_USER_ID, userId);
    console.log("userId: " + userId);

    // Store optional profile data with validation
    if (profile.displayName) {
      storage.setItem(LOCAL_STORAGE_KEYS.LINE_USER_DISPLAY_NAME, profile.displayName);
      console.log("displayName: " + profile.displayName);
    }

    if (profile.pictureUrl) {
      storage.setItem(LOCAL_STORAGE_KEYS.LINE_USER_PICTURE_URL, profile.pictureUrl);
      console.log("pictureUrl: " + profile.pictureUrl);
    }

    if (profile.statusMessage) {
      storage.setItem(LOCAL_STORAGE_KEYS.LINE_USER_STATUS_MESSAGE, profile.statusMessage);
      console.log("statusMessage: " + profile.statusMessage);
    }

    // Handle email from decoded token
    let decodedToken: { email?: string } | null = null;
    try {
      if (window?.liff && typeof window.liff.getDecodedIDToken === 'function') {
        decodedToken = window.liff.getDecodedIDToken() as { email?: string } | null;
        if (decodedToken && decodedToken.email) {
          storage.setItem(LOCAL_STORAGE_KEYS.LINE_USER_EMAIL, decodedToken.email);
          console.log("email: " + decodedToken.email);
        }
      }
    } catch (emailError) {
      console.warn("Failed to get email from decoded token:", emailError);
    }

    // Store language and system info
    try {
      if (window?.liff && typeof window.liff.getLanguage === 'function') {
        const lineLanguage = window.liff.getLanguage();
        if (lineLanguage) {
          storage.setItem(LOCAL_STORAGE_KEYS.LINE_USER_LANGUAGE, lineLanguage);
           console.log("Setting app language from LIFF language:", lineLanguage);
          console.log("getLanguage: " + lineLanguage);
        }
      }
    } catch (langError) {
      console.warn("Failed to get language from LIFF:", langError);
    }

    // Log system info safely
    try {
      if (window?.liff) {
        if (typeof window.liff.getOS === 'function') {
          console.log("getOS: " + window.liff.getOS());
        }
        if (typeof window.liff.getVersion === 'function') {
          console.log("getVersion: " + window.liff.getVersion());
        }
      }
    } catch (systemError) {
      console.warn("Failed to get system info from LIFF:", systemError);
    }

    logger.info('Profile data stored successfully', { 
      userId, 
      hasDisplayName: !!profile.displayName,
      hasPictureUrl: !!profile.pictureUrl,
      hasEmail: !!decodedToken?.email,
      liffId 
    });

    return true;
  } catch (error) {
    logger.error('Failed to store profile data', { error, liffId });
    return false;
  }
};

export function LiffProvider() {

  const liffID = '2007280700-9PzXZGB7';
  const isInitializing = useRef(false);
  const isInitialized = useRef(false);
  const initAttempted = useRef(false);
  const globalTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const sdkRetryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const sdkRetryCount = useRef(0);
  const { setLanguageFromSystem, clearUserLanguageFlag } = useLanguageStore();

  const logger = useLogger('LiffProvider');
  const searchParams = useSearchParams();
  const type = searchParams.get('type');

  const MAX_SDK_RETRIES = 30; // 3 seconds max (30 * 100ms)
  const GLOBAL_TIMEOUT = 60000; // 60 seconds for entire process
  const PERMISSION_TIMEOUT = 45000; // 45 seconds for user permission
  const TECHNICAL_TIMEOUT = 15000; // 15 seconds for technical initialization

  // Cleanup function to clear all timers
  const cleanupTimers = () => {
    if (globalTimeoutRef.current) {
      clearTimeout(globalTimeoutRef.current);
      globalTimeoutRef.current = null;
    }
    if (sdkRetryTimeoutRef.current) {
      clearTimeout(sdkRetryTimeoutRef.current);
      sdkRetryTimeoutRef.current = null;
    }
  };

  // Function to handle initialization failure
  const handleInitializationFailure = (reason: string) => {
    cleanupTimers();
    isInitializing.current = false;
    console.error(`LIFF initialization failed: ${reason}`);
    logger.error(`LIFF initialization failed: ${reason}`, { type });
    window.dispatchEvent(new Event('lineLoginFailure'));
  };

  // Main function to handle user profile with retry logic
  const handleUserProfile = async (liffId: string, retryCount = 0): Promise<void> => {
    const MAX_RETRIES = 3;
    const RETRY_DELAYS = [1000, 2000, 4000]; // Exponential backoff: 1s, 2s, 4s

    try {
      logger.info(`Attempting to fetch profile (attempt ${retryCount + 1}/${MAX_RETRIES + 1})`, { liffId });
      
      let userId: string | null = null;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let profileData: any = {};
      let isPartialProfile = false;

      // Try to get full profile first
      try {
        const profile = await window.liff?.getProfile();
        if (profile && profile.userId) {
          userId = profile.userId;
          profileData = profile;
          console.log("Full profile obtained from getProfile()");
        } else {
          console.warn("Profile obtained but missing userId");
          isPartialProfile = true;
        }
      } catch (profileError) {
        console.warn("getProfile() failed:", profileError);
        isPartialProfile = true;
      }

      // If no userId from profile, try fallback methods
      if (!userId) {
        userId = extractUserIdFromLiff();
        if (userId) {
          isPartialProfile = true;
          console.log("UserId obtained from fallback methods");
        }
      }

      // If we have userId, proceed to store data
      if (userId) {
        const stored = storeProfileData(profileData, userId, logger, liffId);
        
        if (stored) {
          // Set language in store safely
          try {
            if (window?.liff && typeof window.liff.getLanguage === 'function') {
              const lineLanguage = window.liff.getLanguage();
              if (lineLanguage) {
                console.log("Setting app language from LIFF language:", lineLanguage);
                const language = lineLanguage.toLowerCase().includes('th') ? 'th' : 'en';
                console.log("Setting app language:", language);
                clearUserLanguageFlag();
                setLanguageFromSystem(language);
              }
            }
          } catch (langError) {
            console.warn("Failed to set language from LIFF:", langError);
          }

          // Dispatch appropriate success event
          const eventType = isPartialProfile ? 'linePartialSuccess' : 'lineLoginSuccess';
          window.dispatchEvent(new CustomEvent(eventType, { 
            detail: { userId, isPartial: isPartialProfile, profileData } 
          }));
          console.log(`Dispatched ${eventType} event`);
          
          logger.info('Profile handling completed successfully', { 
            userId, 
            isPartial: isPartialProfile, 
            liffId 
          });
          return;
        }
      }

      // If we reach here, we failed to get userId or store data
      throw new Error('Failed to obtain user identifier or store profile data');

    } catch (error) {
      console.error(`Profile fetch attempt ${retryCount + 1} failed:`, error);
      
      // Retry logic with exponential backoff
      if (retryCount < MAX_RETRIES) {
        const delay = RETRY_DELAYS[retryCount];
        logger.warn(`Retrying profile fetch in ${delay}ms (attempt ${retryCount + 2}/${MAX_RETRIES + 1})`, { error, liffId });
        
        setTimeout(async () => {
          await handleUserProfile(liffId, retryCount + 1);
        }, delay);
        return;
      }

      // All retries exhausted
      logger.error('All profile fetch attempts failed', { error, liffId, retryCount });
      window.dispatchEvent(new CustomEvent('lineProfileError', { 
        detail: { error: 'Failed to get user profile after multiple attempts', liffId } 
      }));
    }
  };

  useEffect(() => {
    // Prevent multiple initialization attempts
    if (initAttempted.current) {
      console.log("LIFF initialization already attempted");
      return;
    }
    
    initAttempted.current = true;
    sdkRetryCount.current = 0;

    // Set global timeout for entire initialization process
    globalTimeoutRef.current = setTimeout(() => {
      handleInitializationFailure('Global timeout after 60 seconds');
    }, GLOBAL_TIMEOUT);

    // Create a function to handle LIFF initialization with permission awareness
    const initializeLiff = async () => {
      try {
        if (!window.liff) {
          console.log("LIFF SDK not loaded yet during initialization attempt");
          return; // Exit if LIFF isn't loaded yet
        }

        if (isInitializing.current || isInitialized.current) {
          console.log("LIFF initialization already in progress or completed");
          return;
        }

        isInitializing.current = true;
        console.log("Initializing LIFF with permission detection...");
        logger.info('Starting LIFF initialization');
        const liffId = liffID;

        // Create promises for init, ready, and timeouts
        const initPromise = window.liff.init({ liffId }).then(() => ({ type: 'init', success: true }));
        const readyPromise = window.liff.ready.then(() => ({ type: 'ready', success: true }));
        const permissionTimeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Permission timeout - user may need to approve in Line app')), PERMISSION_TIMEOUT);
        });
        const technicalTimeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Technical timeout - LIFF initialization failed')), TECHNICAL_TIMEOUT);
        });

        console.log("Racing between init, ready, and timeout promises...");
        
        // First race: Check if ready promise resolves quickly (user already gave permission)
        const quickReadyCheck = Promise.race([
          readyPromise,
          new Promise(resolve => setTimeout(() => resolve({ type: 'timeout', success: false }), 1000))
        ]);

        const quickResult = await quickReadyCheck as { type: string; success: boolean };
        
        if (quickResult?.type === 'ready') {
          console.log("User permission already granted, waiting for init to complete...");
          // User already gave permission, just wait for init with shorter timeout
          await Promise.race([initPromise, technicalTimeoutPromise]);
          console.log("LIFF initialized successfully (permission pre-granted)");
          logger.info('LIFF initialized successfully (permission pre-granted)');
        } else {
          console.log("User permission pending, waiting for user interaction or init completion...");
          logger.info('Waiting for user permission during LIFF initialization');
          // Race between init completing and permission timeout
          const result = await Promise.race([initPromise, readyPromise, permissionTimeoutPromise]) as { type: string; success: boolean };
          console.log(`LIFF initialization completed via: ${result.type}`);
          logger.info(`LIFF initialization completed via: ${result.type}`);
        }
        
        // Ensure LIFF is actually ready
        await window.liff.ready;
        
        cleanupTimers(); // Clear global timeout on success
        isInitialized.current = true;
        isInitializing.current = false;
        console.log("LIFF fully initialized and ready");
        
        if (window.liff.isLoggedIn()) {
          console.log("User is logged in, fetching profile....");
          await handleUserProfile(liffId);
        } else {
          logger.warn("User is not logged in, redirecting to login...", {liffId});
          window.liff.login();
        }
      } catch (error) {
        isInitializing.current = false;
        
        // Determine error type for better messaging
        const errorMessage = error instanceof Error ? error.message : String(error);
        let failureReason = `LIFF initialization failed: ${errorMessage}`;
        
        if (errorMessage.includes('Permission timeout')) {
          failureReason = 'User permission required - please approve in Line app and try again';
          logger.warn('LIFF initialization failed due to permission timeout', { 
            error: errorMessage, 
            liffId: liffID,
            hint: 'User may need to approve permissions in Line app'
          });
        } else if (errorMessage.includes('Technical timeout')) {
          failureReason = 'Technical error - LIFF service may be unavailable';
          logger.error('LIFF initialization failed due to technical timeout', { 
            error: errorMessage, 
            liffId: liffID,
            hint: 'Network or LIFF service issue'
          });
        } else {
          logger.error('LIFF initialization failed with unexpected error', { 
            error: errorMessage, 
            liffId: liffID
          });
        }
        
        console.error("LIFF initialization error:", error);
        logger.error(`LIFF initialization error: ${errorMessage}`, { liffID });
        handleInitializationFailure(failureReason);
      }
    };

    // Improved SDK checking with retry limit
    const checkAndInitializeLiff = () => {
      if (window.liff) {
        console.log("LIFF SDK found, initializing...");
        initializeLiff();
        return;
      }
      
      if (sdkRetryCount.current >= MAX_SDK_RETRIES) {
        handleInitializationFailure('LIFF SDK failed to load after 3 seconds');
        return;
      }
      
      sdkRetryCount.current++;
      console.log(`LIFF SDK not available yet, retry ${sdkRetryCount.current}/${MAX_SDK_RETRIES}`);
      logger.warn(`LIFF SDK not available yet, retrying... (${sdkRetryCount.current}/${MAX_SDK_RETRIES})`);
      sdkRetryTimeoutRef.current = setTimeout(checkAndInitializeLiff, 100);
    };

    // Add listeners
    const handleLiffLoaded = () => {
      console.log("LIFF SDK loaded event received");
      if (sdkRetryTimeoutRef.current) {
        clearTimeout(sdkRetryTimeoutRef.current);
        sdkRetryTimeoutRef.current = null;
      }
      initializeLiff();
    };

    const handleRetryRequest = () => {
      console.log('Retry requested, clearing state and retrying...');
      cleanupTimers();
      isInitializing.current = false;
      isInitialized.current = false;
      initAttempted.current = false;
      sdkRetryCount.current = 0;
      
      // Restart the process
      setTimeout(() => {
        initAttempted.current = true;
        globalTimeoutRef.current = setTimeout(() => {
          handleInitializationFailure('Global timeout after retry');
        }, GLOBAL_TIMEOUT);
        checkAndInitializeLiff();
      }, 500);
    };

    window.addEventListener('liff-loaded', handleLiffLoaded);
    window.addEventListener('liffRetryRequested', handleRetryRequest);
    
    // Start the initialization process
    checkAndInitializeLiff();

    // Cleanup function
    return () => {
      cleanupTimers();
      window.removeEventListener('liff-loaded', handleLiffLoaded);
      window.removeEventListener('liffRetryRequested', handleRetryRequest);
    };
  }, []); // Remove logger and type dependencies to prevent re-runs

  return (
    <>
      <Script 
        src="https://static.line-scdn.net/liff/edge/versions/2.21.0/sdk.js" 
        strategy="afterInteractive"
        onLoad={() => {
          console.log("LIFF SDK loaded");
          // Dispatch custom event when LIFF SDK is loaded
          window.dispatchEvent(new Event('liff-loaded'));
        }}
      />
    </>
  );
}